import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import { documentService } from '../services/documentService';
import { useLanguage } from '../i18n';

// Shared voice-clip player (extracted from Message.jsx so the pre-send composer
// preview can reuse the EXACT same player as the sent chat message — Matching
// audio files #139). One clip = a circular play/pause button, a colorless
// deterministic waveform, and the current/total time. A hidden <audio> drives real
// playback; bars left of the playhead are highlighted; click-to-seek.

export const formatVoiceDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// A deterministic pseudo-random bar set seeded from a stable string (the clip's
// gcs_path/audio_url) so the waveform looks distinct per clip but never changes
// between renders. We render the LOOK of a voice waveform (decoding real amplitude
// is unnecessary for the mockup's "colorless, integrated" aesthetic and would mean
// fetching+decoding the audio bytes on every bubble). Heights are eased at the ends
// so it reads as speech.
export const WAVE_BAR_COUNT = 60;
export const waveformBars = (seed) => {
    let s = 0;
    const key = String(seed || 'voice');
    for (let i = 0; i < key.length; i++) s = (Math.imul(s, 31) + key.charCodeAt(i)) >>> 0;
    const rand = () => {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    return Array.from({ length: WAVE_BAR_COUNT }, (_, i) => {
        // Taper the ends so the waveform fades in/out like a real clip.
        const envelope = Math.sin((Math.PI * (i + 0.5)) / WAVE_BAR_COUNT);
        const h = (0.18 + 0.82 * rand()) * (0.45 + 0.55 * envelope);
        return Math.max(0.12, Math.min(1, h));
    });
};

// One audio clip — a custom waveform player row (Voice Message Final Design #87).
// `segment` shape: { audio_url, gcs_path?, duration? }. Reused unchanged by the
// chat bubble (VoiceBubble), the edit-mode segment manager, AND the pre-send
// composer preview (#139).
export const VoiceClipPlayer = ({ segment }) => {
    const { t } = useLanguage();
    const audioRef = useRef(null);
    const [audioError, setAudioError] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(segment?.duration || 0);
    // Playback URLs are HMAC-signed and expire (24h); legacy messages carry the
    // old ?user_id= form which the BE now rejects. On the first <audio> error we
    // mint a fresh signed URL from the stored gcs_path and retry once.
    const [src, setSrc] = useState(segment?.audio_url || null);
    const refreshTriedRef = useRef(false);

    // Keep `src` in sync when the caller swaps `audio_url` (e.g. the composer preview
    // upgrades a local blob URL to the server signed URL once transcription lands).
    // Guarded so a freshly-refreshed URL (error path below) is never clobbered. In
    // the chat bubble `audio_url` is stable, so this is a harmless no-op there.
    useEffect(() => {
        if (!refreshTriedRef.current) setSrc(segment?.audio_url || null);
    }, [segment?.audio_url]);

    const handleAudioError = async () => {
        if (!refreshTriedRef.current && segment?.gcs_path) {
            refreshTriedRef.current = true;
            const fresh = await documentService.refreshAudioUrl(segment.gcs_path);
            if (fresh) {
                setSrc(fresh);
                return;
            }
        }
        setAudioError(true);
    };

    const bars = useMemo(
        () => waveformBars(segment?.gcs_path || segment?.audio_url || 'voice'),
        [segment?.gcs_path, segment?.audio_url],
    );
    const progress = duration > 0 ? Math.min(1, current / duration) : 0;

    const togglePlay = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) a.play().catch(() => setAudioError(true));
        else a.pause();
    };

    const seek = (e) => {
        const a = audioRef.current;
        if (!a || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        a.currentTime = ratio * duration;
        setCurrent(a.currentTime);
    };

    if (audioError) {
        return (
            <div data-testid="voice-clip" className="text-xs text-pai-faint italic py-2 text-center bg-white/[0.06] rounded-lg">
                {t('msgVoiceAudioUnavailable', 'Audio unavailable (link expired)')}
            </div>
        );
    }

    return (
        <div
            data-testid="voice-clip"
            // Ui responsive Layout (#243, 2nd pass): the player must be intrinsically
            // SHRINKABLE. `min-w-0 max-w-full` stops it forcing its parent wider than the
            // bubble, and `flex-wrap` lets the time readout drop to its own line at the
            // narrowest layout instead of bleeding past the pill border (the reported bug).
            // gap-x-3 preserves the exact horizontal rhythm of the one-line layout.
            // `overflow-hidden` is the structural backstop: below the supported ~240px
            // layout the user bubble's reply/edit gutter (#219/#224) can leave a content
            // box narrower than the readout itself, and nothing may paint outside the pill.
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-pai-subtle px-3 py-2 min-w-0 max-w-full overflow-hidden"
        >
            <button
                type="button"
                onClick={togglePlay}
                data-testid="voice-playpause"
                aria-label={playing ? t('msgVoicePause', 'Pause') : t('msgVoicePlay', 'Play')}
                className="shrink-0 w-9 h-9 rounded-full border border-white/[0.24] bg-white/[0.06] flex items-center justify-center text-white hover:bg-white/[0.13] transition-colors"
            >
                {playing ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" className="ml-0.5" />}
            </button>
            <div
                data-testid="voice-waveform"
                onClick={seek}
                // `min-w-0` is load-bearing (#243): without it this flex item's automatic
                // min-width is its min-content (60 bars x 1px + gaps ~= 178px), a hard floor
                // that made the whole row refuse to shrink and pushed the time readout out
                // of the bubble. With it the waveform compresses (overflow-hidden clips the
                // tail bars) and the row always fits.
                className="flex-1 min-w-0 flex items-center gap-[2px] h-8 cursor-pointer overflow-hidden"
            >
                {bars.map((h, i) => {
                    const played = i / bars.length <= progress;
                    return (
                        <span
                            key={i}
                            className={`flex-1 rounded-full ${played ? 'bg-white/85' : 'bg-white/[0.28]'}`}
                            style={{ height: `${Math.round(h * 100)}%`, minWidth: '1px' }}
                        />
                    );
                })}
            </div>
            <span data-testid="voice-clip-time" className="shrink-0 whitespace-nowrap text-[11px] text-white/60 tabular-nums">
                {formatVoiceDuration(current)} / {formatVoiceDuration(duration)}
            </span>
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                className="hidden"
                onLoadedMetadata={(e) => {
                    const d = e.target.duration;
                    if (Number.isFinite(d) && d > 0) setDuration(d);
                }}
                onTimeUpdate={(e) => setCurrent(e.target.currentTime || 0)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => { setPlaying(false); setCurrent(0); }}
                onError={handleAudioError}
            />
        </div>
    );
};

export default VoiceClipPlayer;
